import { IComponentOptions } from "angular";
import { StateService } from "@uirouter/angularjs";
import { IGridAuditLog } from "@gtmhub/configuration";
import { GtmhubController } from "@gtmhub/core";
import { IGHGridColumn } from "@gtmhub/shared/components/grid/grid.component";
import { checkOverflowOfColumnWidth } from "@gtmhub/shared/utils/tooltips-util";

export interface IALCellCtrlCellComponentBindings {
  item: IGridAuditLog;
  column: IGHGridColumn;
}

// eslint-disable-next-line @typescript-eslint/no-empty-interface
export interface ALCellCtrl extends IALCellCtrlCellComponentBindings {}

export class ALCellCtrl extends GtmhubController {
  static $inject = ["$state"];

  constructor(private $state: StateService) {
    super();
  }

  shouldEnableTooltip(): boolean {
    const childId = `${this.column.fieldName}-${this.item.id}`;
    const parentColumn = this.column.fieldName;

    if (this.column.type === "user") {
      const column = document.querySelector(`[data-tooltip-trigger-id='${parentColumn}']`).clientWidth;
      const elementWidth = document.getElementById(childId).parentElement.offsetWidth - 16;
      return column < elementWidth;
    }

    return checkOverflowOfColumnWidth(childId, parentColumn);
  }

  getTooltip(): string {
    return this.item.type === "user" ? this.item.user.name : this.item.item;
  }

  // eslint-disable-next-line sonarjs/cognitive-complexity
  goToState = (type: string, action: string): void => {
    if ((this.item.isDeleted !== undefined && this.item.isDeleted) || (this.item.currentUserHasAccess !== undefined && !this.item.currentUserHasAccess)) {
      return;
    }

    let url;
    // eslint-disable-next-line sonarjs/max-switch-cases
    switch (type) {
      case "automation": {
        url = this.$state.href("gtmhub.automation.rules", { automationId: this.item.targetId });
        break;
      }
      case "goal":
      case "objective_link": {
        url = this.$state.href("gtmhub.goals.all._goalSidebar.goal", {
          planningSessionId: this.item.targetId,
          id: this.item.targetId,
        });
        break;
      }
      case "metric": {
        url = this.$state.href("gtmhub.goals.all._metric.metric", { metricId: this.item.targetId });
        break;
      }
      case "team":
      case "team_manager":
      case "team_member": {
        url = this.$state.href("gtmhub.teams.team", { teamId: this.item.targetId });
        break;
      }
      case "user": {
        url = this.$state.href("gtmhub.usersList");
        break;
      }
      case "task": {
        url = this.$state.href("gtmhub.tasks");
        break;
      }
      case "kpi": {
        if (action === "Created") {
          url = this.$state.href("gtmhub.kpis");
        } else {
          url = this.$state.href("gtmhub.kpis._singleKpi.form", { kpiId: this.item.targetId });
        }
        break;
      }
      case "kpi_group": {
        url = this.$state.href("gtmhub.kpis");
        break;
      }
      case "badge": {
        url = this.$state.href("gtmhub.badges");
        break;
      }
      case "custom_field": {
        url = this.$state.href("gtmhub.configuration.customFields");
        break;
      }
      case "dashboard": {
        if (action === "Created") {
          url = this.$state.href("gtmhub.insightboards");
        } else {
          url = this.$state.href("gtmhub.insightboards", { dashboardId: this.item.targetId });
        }
        break;
      }
      case "api_token_info": {
        url = this.$state.href("gtmhub.configuration.apiTokens");
        break;
      }
      case "session": {
        if (action === "Created") {
          url = this.$state.href("gtmhub.sessions");
        } else {
          url = this.$state.href("gtmhub.sessions._form.form", { id: this.item.targetId });
        }
        break;
      }
      case "datasource": {
        if (action === "Created") {
          url = this.$state.href("gtmhub.dataflow");
        } else {
          url = this.$state.href("gtmhub.dataflow._editRight.connection", {
            dataSourceBlueprintName: this.item.targetId,
            connectionId: this.item.targetId,
          });
        }
        break;
      }
      case "plugin":
      case "plugin version": {
        url = this.$state.href("/marketplace");
        break;
      }
      case "ad_connection": {
        url = this.$state.href("gtmhub.configuration.singleSignOn");
        break;
      }
      case "dashboard_parameter":
      case "dashboard_tag": {
        url = this.$state.href("gtmhub.insightboards", { dashboardId: this.item.targetId });
        break;
      }
      case "account": {
        url = this.$state.href("gtmhub.configuration.accountNameDomainChange");
        break;
      }
      case "insight": {
        url = this.$state.href("gtmhub.insightboards");
        break;
      }
      case "comment": {
        break;
      }
      case "attachment": {
        url = this.$state.href("gtmhub.tasks");
        break;
      }
      case "connection": {
        url = this.$state.href("gtmhub.dataflow");
        break;
      }
      case "goal_templates": {
        url = this.$state.href("/marketplace");
        break;
      }
      case "insight_templates": {
        url = this.$state.href("gtmhub.insightboards");
        break;
      }
      case "insight_board_templates": {
        url = this.$state.href("gtmhub.insightboards");
        break;
      }
      case "integration": {
        url = this.$state.href("gtmhub.configuration.slack");
        break;
      }
      case "list": {
        url = this.$state.href("gtmhub.lists");
        break;
      }
      case "notification_settings": {
        url = this.$state.href("gtmhub.userNotificationSettings");
        break;
      }
      case "role": {
        url = this.$state.href("gtmhub.roles");
        break;
      }
      case "tags": {
        url = this.$state.href("gtmhub.configuration.tags");
        break;
      }
      case "whiteboard": {
        if (action === "Created") {
          url = this.$state.href("gtmhub.whiteboards");
        } else {
          url = this.$state.href("gtmhub.whiteboard", { whiteboardId: this.item.targetId });
        }
        break;
      }
      case "workday_settings": {
        url = this.$state.href("gtmhub.configuration.workday.settings");
        break;
      }
      default: {
        return;
      }
    }

    window.open(url, "_blank");
  };
}

export const AuditLogCellComponent: IComponentOptions = {
  bindings: {
    item: "<",
    column: "<",
  },
  controller: ALCellCtrl,
  templateUrl: require("./al-cell.component.jade"),
};
